import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getWarehouses } from '../../services/warehousesService';
import { authService } from '../../services/authService';
import { filterWarehousesForUser } from '../../utils/limitedUserAccess';
import Toast from '../../components/Common/Toast';

const SelectWarehouse = () => {
  const navigate = useNavigate();
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });

  useEffect(() => {
    loadWarehouses();
  }, []);

  const loadWarehouses = async () => {
    try {
      setLoading(true);
      const response = await getWarehouses({ active_only: true });
      
      if (response.success) {
        setWarehouses(
          filterWarehousesForUser(response.data || [], authService.getCurrentUser())
        );
      } else {
        setToast({
          isOpen: true,
          message: 'Error al cargar los almacenes',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error al cargar almacenes:', error);
      setToast({
        isOpen: true,
        message: 'Error al cargar los almacenes',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSelectWarehouse = (warehouseId) => {
    navigate(`/warehouses/${warehouseId}/documents`);
  };

  const filteredWarehouses = warehouses.filter(warehouse =>
    warehouse.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    warehouse.code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-6">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-3xl font-bold text-gray-800">Almacenes</h1>
                <p className="text-gray-600 mt-1">Seleccione un almacén para ver sus documentos</p>
              </div>
            </div>

            {/* Breadcrumb */}
            <nav className="text-sm text-gray-600 mb-4">
              <span className="hover:text-teal-600 cursor-pointer" onClick={() => navigate('/dashboard')}>Dashboard</span>
              <span className="mx-2">/</span>
              <span className="text-gray-800">Almacenes</span>
            </nav>

            {/* Búsqueda */}
            <div className="bg-white rounded-lg shadow-md p-5 mb-6">
              <input
                type="text"
                placeholder="Buscar almacén por código o nombre..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
          </div>

          {/* Lista de Almacenes */}
          {loading ? (
            <div className="text-center py-12 bg-white rounded-lg shadow-md">
              <div className="text-gray-500">Cargando almacenes...</div>
            </div>
          ) : filteredWarehouses.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg shadow-md">
              <p className="text-gray-500">
                {searchTerm ? 'No se encontraron almacenes con ese criterio' : 'No hay almacenes activos disponibles'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredWarehouses.map((warehouse) => (
                <div
                  key={warehouse.id}
                  onClick={() => handleSelectWarehouse(warehouse.id)}
                  className="bg-white rounded-lg shadow-md p-6 cursor-pointer hover:shadow-lg transition-shadow border border-gray-200 hover:border-teal-300"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-800 mb-1">{warehouse.name}</h3>
                      <p className="text-sm font-mono text-teal-600">{warehouse.code}</p>
                    </div>
                    <div className="ml-4">
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                        Activo
                      </span>
                    </div>
                  </div>
                  
                  {warehouse.address && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <p className="text-xs text-gray-500 mb-1">Dirección</p>
                      <p className="text-sm text-gray-700">{warehouse.address}</p>
                    </div>
                  )}

                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="flex items-center text-teal-600 text-sm font-medium">
                      <span>Ver documentos</span>
                      <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Toast */}
      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, isOpen: false })}
      />
    </div>
  );
};

export default SelectWarehouse;

